"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Zap, Target, ChartBar } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AnimatedCounter({
  target,
  suffix = ""
}: {
  target: number;
  suffix?: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    once: true
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number;
    const duration = 2000;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * target));
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, target]);
  return <span ref={ref} className="tabular-nums">
      {count}
      {suffix}
    </span>;
}
const statsData = [{
  label: "Expérience",
  value: 10,
  suffix: "+ Ans",
  description: "Expertise digitale confirmée",
  icon: <Zap className="size-6 text-primary" />,
  color: "text-primary"
}, {
  label: "Projets Réalisés",
  value: 500,
  suffix: "+",
  description: "Succès clients Smilegeeks",
  icon: <Target className="size-6 text-secondary" />,
  color: "text-secondary"
}, {
  label: "Croissance Moyenne",
  value: 300,
  suffix: "%",
  description: "Performance SEO & Marketing",
  icon: <ChartBar className="size-6 text-primary" />,
  color: "text-primary"
}];
export default function MeasurableInclusion() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true
  });
  return <section data-section-id="224145" id="impact-en-chiffres" className="bg-[#050505] text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_center,black,transparent)] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-lg blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 lg:mb-24 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 border border-primary/20 text-primary font-mono text-xs tracking-widest uppercase mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-lg h-2 w-2 bg-primary"></span>
            </span>
            Performance Metrics
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            Impact en <span className="text-primary">Chiffres</span>
          </h2>
          <p className="text-slate-400 text-lg md:text-xl font-sans leading-relaxed">
            Des résultats concrets portés par l'innovation technologique et une
            vision stratégique du marketing digital.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {statsData.map((stat, i) => <div key={i} data-index={i} className={`transition-all duration-1000 delay-${i * 200} transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <Card className="border border-white/10 bg-[#121212]/40 backdrop-blur-xl shadow-2xl shadow-primary/5 h-full flex flex-col justify-center items-center text-center rounded-2xl hover:border-primary/50 transition-all duration-500 group relative overflow-hidden">
                {}
                <div className="absolute inset-0 bg-radial-gradient(circle_at_center,var(--color-primary)_0%,transparent_70%) opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none" />
                
                <CardContent className="p-8 flex flex-col items-center gap-6">
                  <div className="p-4 rounded-2xl bg-white/5 border border-white/10 group-hover:border-primary/30 transition-colors duration-500 relative overflow-hidden animate-bounce [animation-duration:3s]">
                    {}
                    <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12" />
                    {stat.icon}
                  </div>
                  
                  <div className={`font-mono text-5xl lg:text-6xl font-bold tracking-tighter ${stat.color} transition-colors duration-500`}>
                    <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-serif text-xl font-semibold leading-tight text-white">
                      {stat.label}
                    </h3>
                    <p className="text-slate-400 text-sm font-sans leading-relaxed">
                      {stat.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}